import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Eye, Users, Globe, MapPin, TrendingUp, FileText } from "lucide-react";
import { RecentVisits } from "@/components/TrafficVisits";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium"><Icon className="h-4 w-4 text-redline-500" strokeWidth={1.75} /> {label}</div>
      <div className="font-heading text-3xl font-extrabold mt-2">{value?.toLocaleString?.() ?? value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function RankList({ title, icon: Icon, items, render }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5">
      <div className="font-heading font-bold flex items-center gap-2 mb-4"><Icon className="h-4 w-4 text-redline-500" /> {title}</div>
      {items.length === 0 && <div className="text-sm text-muted-foreground">No data yet.</div>}
      <div className="space-y-2.5">
        {items.map((it, i) => (
          <div key={i}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="truncate pr-2">{render(it)}</span>
              <span className="font-semibold shrink-0">{it.count}</span>
            </div>
            <div className="h-1.5 rounded-full bg-sand-100 overflow-hidden">
              <div className="h-full bg-redline-500 rounded-full" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Traffic() {
  const [days, setDays] = useState(30);
  const [d, setD] = useCachedState(`traffic:${days}`, null);

  useEffect(() => { api.get(`/admin/traffic?days=${days}`).then((r) => setD(r.data)); }, [days]);

  if (!d) return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><TrendingUp className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Site Traffic</>} about="Visitor analytics for the marketing site and app." />
        </div>
        <div className="flex gap-2">
          {[[7, "7d"], [30, "30d"], [90, "90d"]].map(([v, l]) => (
            <button key={v} data-testid={`traffic-range-${v}`} onClick={() => setDays(v)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${days === v ? "bg-ink text-white" : "bg-white border border-sand-200 hover:border-ink"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Page views" value={d.total_visits} sub={`Last ${d.days} days`} />
        <StatCard icon={Users} label="Unique visitors" value={d.unique_visitors} />
        <StatCard icon={Globe} label="Countries" value={d.by_country.length} />
        <StatCard icon={MapPin} label="Cities" value={d.top_cities.length} />
      </div>

      <div className="bg-white rounded-2xl border border-sand-200 p-5">
        <div className="font-heading font-bold mb-4">Daily page views</div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={d.daily} margin={{ left: -20, right: 8, top: 8 }}>
            <defs>
              <linearGradient id="rl" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D32F2F" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#D32F2F" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DF" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8A8178" }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#8A8178" }} allowDecimals={false} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EDE7DF", fontSize: 13 }} />
            <Area type="monotone" dataKey="visits" stroke="#D32F2F" strokeWidth={2.5} fill="url(#rl)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <RankList title="Top countries" icon={Globe} items={d.by_country} render={(it) => it.name || "Unknown"} />
        <RankList title="Top states / regions" icon={MapPin} items={d.by_region} render={(it) => it.name || "Unknown"} />
        <div className="bg-white rounded-2xl border border-sand-200 p-5">
          <div className="font-heading font-bold flex items-center gap-2 mb-4"><MapPin className="h-4 w-4 text-redline-500" /> Top cities</div>
          {d.top_cities.length === 0 && <div className="text-sm text-muted-foreground">No data yet.</div>}
          <div className="space-y-2.5">
            {d.top_cities.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate pr-2">{c.city}{c.region ? `, ${c.region}` : ""} <span className="text-muted-foreground">· {c.country}</span></span>
                <span className="font-semibold shrink-0">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
        <RankList title="Top pages" icon={FileText} items={d.by_path} render={(it) => it.name} />
      </div>

      <RecentVisits days={days} />
    </div>
  );
}
